import { type AxiosResponse } from 'axios';
import { setSubmitFailed, setSubmitSucceeded, startSubmit } from 'redux-form';
import { call, put, takeLatest } from 'redux-saga/effects';

import { ActionKeys } from '~/actions/constants';
import * as lineItemActions from '~/actions/line-item.actions';
import * as ordersActions from '~/actions/orders.actions';
import { LINE_ITEM_FORM } from '~/constants/form';
import * as lineItemService from '~/services/line-item.service';
import * as ordersService from '~/services/orders.service';
import { payloadToJSON } from '~/types/LineItem';
import { batchEntryExtendedFromJSON, type JSONBatchEntryExtended } from '~/types/OrderBatchEntry';

import { synchronizeLineItemToOrdersV2 } from '../orders/orders-v2-sync.util';

function* deleteLineItem(action: ReturnType<typeof lineItemActions.deleteLineItem>) {
  try {
    yield put(startSubmit(LINE_ITEM_FORM));

    yield call(ordersService.createBatchEntriesForOrders, [action.payload.orderObjectId]);

    yield call(lineItemService.deleteLineItem, action.payload.id, action.payload.orderObjectId);
    yield put(ordersActions.fetchOrder(action.payload.orderObjectId));
    yield call(synchronizeLineItemToOrdersV2, {
      action: 'delete',
      lineItemObjectId: action.payload.id,
      orderObjectId: action.payload.orderObjectId,
    });

    const updatedBatchEntries: AxiosResponse<JSONBatchEntryExtended[]> = yield call(
      ordersService.createBatchEntriesForOrders,
      [action.payload.orderObjectId],
    );

    const parsedBatchEntries = updatedBatchEntries.data.map(batchEntryExtendedFromJSON);

    yield put(ordersActions.fetchOrderBatchEntriesSuccess(parsedBatchEntries));

    if (action.payload.refreshBatchEntry) {
      yield put(
        ordersActions.retryShipment({
          [action.payload.orderObjectId]: parsedBatchEntries.map((be) => be.id),
        }),
      );
    }

    yield put(ordersActions.startBatchEntriesPolling([action.payload.orderObjectId]));

    yield put(lineItemActions.deleteLineItemSuccess());
    yield put(setSubmitSucceeded(LINE_ITEM_FORM));
  } catch (e) {
    yield put(setSubmitFailed(LINE_ITEM_FORM));
    yield put(lineItemActions.deleteLineItemError());
  }
}

function* createLineItem(action: ReturnType<typeof lineItemActions.createLineItem>) {
  try {
    yield put(startSubmit(LINE_ITEM_FORM));

    yield call(ordersService.createBatchEntriesForOrders, [action.payload.orderObjectId]);

    const response: AxiosResponse<lineItemService.LineItemResponse> = yield call(
      lineItemService.createLineItem,
      action.payload.orderObjectId,
      payloadToJSON(action.payload.lineItemPayload),
    );
    yield put(ordersActions.fetchOrder(action.payload.orderObjectId));
    yield call(synchronizeLineItemToOrdersV2, {
      action: 'create',
      lineItem: response.data,
      orderObjectId: action.payload.orderObjectId,
    });

    const updatedBatchEntries: AxiosResponse<JSONBatchEntryExtended[]> = yield call(
      ordersService.createBatchEntriesForOrders,
      [action.payload.orderObjectId],
    );

    const parsedBatchEntries = updatedBatchEntries.data.map(batchEntryExtendedFromJSON);

    yield put(ordersActions.fetchOrderBatchEntriesSuccess(parsedBatchEntries));

    if (action.payload.refreshBatchEntry) {
      yield put(
        ordersActions.retryShipment({
          [action.payload.orderObjectId]: parsedBatchEntries.map((be) => be.id),
        }),
      );
    }

    yield put(ordersActions.startBatchEntriesPolling([action.payload.orderObjectId]));
    yield put(lineItemActions.createLineItemSuccess());
    yield put(setSubmitSucceeded(LINE_ITEM_FORM));
  } catch (err) {
    yield put(setSubmitFailed(LINE_ITEM_FORM));
    yield put(lineItemActions.createLineItemError());
  }
}

function* editLineItem(action: ReturnType<typeof lineItemActions.editLineItem>) {
  try {
    yield put(startSubmit(LINE_ITEM_FORM));

    yield call(ordersService.createBatchEntriesForOrders, [action.payload.orderObjectId]);

    const response: AxiosResponse<lineItemService.LineItemResponse> = yield call(
      lineItemService.editLineItem,
      action.payload.lineItemObjectId,
      action.payload.orderObjectId,
      payloadToJSON(action.payload.lineItemPayload),
    );
    yield put(ordersActions.fetchOrder(action.payload.orderObjectId));
    yield call(synchronizeLineItemToOrdersV2, {
      action: 'edit',
      lineItem: response.data,
      orderObjectId: action.payload.orderObjectId,
    });

    const updatedBatchEntries: AxiosResponse<JSONBatchEntryExtended[]> = yield call(
      ordersService.createBatchEntriesForOrders,
      [action.payload.orderObjectId],
    );

    const parsedBatchEntries = updatedBatchEntries.data.map(batchEntryExtendedFromJSON);

    yield put(ordersActions.fetchOrderBatchEntriesSuccess(parsedBatchEntries));

    if (action.payload.refreshBatchEntry) {
      yield put(
        ordersActions.retryShipment({
          [action.payload.orderObjectId]: parsedBatchEntries.map((be) => be.id),
        }),
      );
    }

    yield put(ordersActions.startBatchEntriesPolling([action.payload.orderObjectId]));
    yield put(lineItemActions.editLineItemSuccess());
    yield put(setSubmitSucceeded(LINE_ITEM_FORM));
  } catch (err) {
    yield put(setSubmitFailed(LINE_ITEM_FORM));
    yield put(lineItemActions.editLineItemError());
  }
}

export default [
  takeLatest(ActionKeys.LINE_ITEMS_DELETE, deleteLineItem),
  takeLatest(ActionKeys.LINE_ITEM_CREATE, createLineItem),
  takeLatest(ActionKeys.LINE_ITEM_EDIT, editLineItem),
];
